/*
 * Adds a button that toggles enhanced audio on the player.
 * The audio goes through an equalizer, compressor,  limiter, and a noise gate.
 */

import videojs from 'video.js'
import './enhanced_audio_button.scss'

const Button = videojs.getComponent('Button')
class EnhancedAudioButton extends Button {
  constructor(player, options) {
    super(player, options)

    this.isActive = window.UserPreference.get('enhanced_audio') || false
    this.updateAudioContext()
  }

  handleClick(e) {
    this.isActive = !this.isActive
    window.UserPreference.set('enhanced_audio', this.isActive)
    this.updateAudioContext()
  }

  buildCSSClass() {
    return `vjs-enhanced-audio-button ${
      this.isActive ? 'vjs-enhanced-audio-button-active' : ''
    } ${super.buildCSSClass()}`
  }

  updateAudioContext() {
    this.toggleClass('vjs-enhanced-audio-button-active', this.isActive)
    const paused = this.player().paused()

    if (paused) {
      this.player().one('play', this.toggleEnhancedAudio)
    } else {
      this.toggleEnhancedAudio()
    }
  }

  toggleEnhancedAudio = () => {
    if (this.isActive) {
      this.enableEnhancedAudio()
    } else {
      this.disableEnhancedAudio()
    }
  }

  setupEnhancedAudio = () => {
    const audioCtx = new AudioContext()

    const source = new MediaElementAudioSourceNode(audioCtx, {
      mediaElement: this.player().tech().el(),
    })

    // Equalizer
    const eq = audioCtx.createBiquadFilter()
    eq.type = 'peaking'
    eq.frequency.value = 1000 // Center frequency for mid-range boost
    eq.gain.value = 3 // Boost mid-range frequencies

    // Noise Gate
    const noiseGate = new DynamicsCompressorNode(audioCtx, {
      threshold: -60,
      knee: 0,
      ratio: 1,
      attack: 0.01,
      release: 0.1,
    })

    // Compressor
    const compressor = new DynamicsCompressorNode(audioCtx, {
      threshold: -50,
      knee: 40,
      ratio: 6,
      attack: 0.01, // Attack time in seconds
      release: 0.3, // Release time in seconds
    })

    // Limiter
    const limiter = new DynamicsCompressorNode(audioCtx, {
      threshold: -6,
      knee: 0,
      ratio: 10,
      attack: 0.01,
      release: 0.2,
    })

    const chain = [eq, noiseGate, compressor, limiter]

    this.connectNodes = () => {
      source.disconnect()
      source.connect(chain[0])
      for (let i = 0; i < chain.length - 1; i++) {
        chain[i].connect(chain[i + 1])
      }
      chain[chain.length - 1].connect(audioCtx.destination)
    }

    this.disconnectNodes = () => {
      source.disconnect()
      chain.forEach((node) => node.disconnect())
      source.connect(audioCtx.destination)
    }
  }

  enableEnhancedAudio = () => {
    if (!this.connectNodes) this.setupEnhancedAudio()
    this.connectNodes()
  }

  disableEnhancedAudio = () => {
    if (this.disconnectNodes) this.disconnectNodes()
  }
}

function checkIfWebKit() {
  var ua = navigator.userAgent.toLowerCase()

  var isWebKit = false

  if (
    ua.indexOf('chrome') === ua.indexOf('android') &&
    ua.indexOf('safari') !== -1
  ) {
    // accessed via a WebKit-based browser
    isWebKit = true
  } else {
    // check if accessed via a WebKit-based webview
    if (
      ua.indexOf('ipad') !== -1 ||
      ua.indexOf('iphone') !== -1 ||
      ua.indexOf('ipod') !== -1
    ) {
      isWebKit = true
    } else {
      isWebKit = false
    }
  }

  return isWebKit
}

EnhancedAudioButton.prototype.controlText_ = 'Enhanced Audio'

videojs.registerComponent('EnhancedAudioButton', EnhancedAudioButton)

videojs.registerPlugin('enhancedAudio', function () {
  if (checkIfWebKit()) return
  if (!window.UserPreference) return
  if (!window.SUPPORTS_FONT_AWESOME) return

  const player = this
  player.ready(() => {
    const controlBar = player.controlBar
    let newIndex
    if (player.isAudio()) {
      const playbackRateMenu = controlBar.getChild('PlaybackRateMenuButton')
      newIndex = controlBar.children().indexOf(playbackRateMenu) + 1
    } else {
      const qualitySwitcher = controlBar.getChild('SourceMenuButton')
      const pip = controlBar.getChild('PictureInPictureToggle')
      newIndex = controlBar.children().indexOf(qualitySwitcher || pip)
    }
    controlBar.addChild('EnhancedAudioButton', {}, newIndex)
  })
})
